// 🔧 content_scopes.js
// ✅ Purpose: Defines where a Content Engine item applies within the LGV platform
// ✅ Features:
// - Supports global content today
// - Prepares for future organization white-labeling
// - Prepares for future event overrides
// - Prepares for localization, market, and profile-specific content
// - Used by the Content Resolver to determine fallback order
// - Designed to avoid future schema changes as LGV expands

/**
 * -------------------------------------------------------------------------
 * Canonical Content Scopes
 * -------------------------------------------------------------------------
 *
 * Global
 *   Platform-wide default content.
 *
 * Organization
 *   Future white-label organization overrides.
 *
 * Event
 *   Event-specific overrides.
 *
 * Locale
 *   Language-specific content.
 *
 * Profile
 *   Audience / market profile variations.
 *
 * Market
 *   Market-specific messaging (Automotive, Education, Government, etc.)
 *
 * -------------------------------------------------------------------------
 */

export const CONTENT_SCOPES = {
  GLOBAL: "global",
  ORGANIZATION: "organization",
  EVENT: "event",
  LOCALE: "locale",
  PROFILE: "profile",
  MARKET: "market",
};

/**
 * Human-friendly labels for admin UI.
 */
export const CONTENT_SCOPE_LABELS = {
  [CONTENT_SCOPES.GLOBAL]: "Global",
  [CONTENT_SCOPES.ORGANIZATION]: "Organization",
  [CONTENT_SCOPES.EVENT]: "Event",
  [CONTENT_SCOPES.LOCALE]: "Locale",
  [CONTENT_SCOPES.PROFILE]: "Profile",
  [CONTENT_SCOPES.MARKET]: "Market",
};

/**
 * -------------------------------------------------------------------------
 * Default Scope Values
 * -------------------------------------------------------------------------
 *
 * These values allow Phase 1 to be fully global while preparing
 * the schema for future organization, event, and localization support.
 */

export const DEFAULT_CONTENT_SCOPE = CONTENT_SCOPES.GLOBAL;

export const DEFAULT_SCOPE_ID = "global";

/**
 * -------------------------------------------------------------------------
 * Future Override Hierarchy
 * -------------------------------------------------------------------------
 *
 * This order has been architecturally approved.
 *
 * The resolver will eventually attempt lookups in this order:
 *
 * Organization + Locale + Profile
 * ↓
 * Organization + Locale
 * ↓
 * Organization Default
 * ↓
 * Global + Locale + Profile
 * ↓
 * Global + Locale
 * ↓
 * Global Default (en-US)
 * ↓
 * Code Fallback
 *
 * NOTE:
 * Phase 1 only implements Global scope.
 * The remaining scopes are intentionally included now so the
 * schema never requires redesign.
 */

export const CONTENT_OVERRIDE_ORDER = [
  "organization_locale_profile",
  "organization_locale",
  "organization_default",
  "global_locale_profile",
  "global_locale",
  "global_default",
  "code_default",
];